import React, { useRef, useEffect, useState } from 'react';
import { Bold, Italic, Underline, Type } from 'lucide-react';

/**
 * Rich Text Editor Component for Assessment Builder
 * 
 * Provides a simple WYSIWYG editor for formatting question text and response options
 * with bold, italic, and underline styling. Sanitizes input to prevent XSS attacks.
 * 
 * Features:
 * - Formatting toolbar with bold, italic, underline buttons
 * - Keyboard shortcuts (Ctrl+B, Ctrl+I, Ctrl+U)
 * - Clear formatting button
 * - Paste sanitization (strips unwanted tags)
 * - Active state indicators for current formatting
 * 
 * @param {string} value - Current HTML content
 * @param {function} onChange - Callback when content changes
 * @param {string} placeholder - Placeholder text when empty
 * @param {string} label - Optional label above editor
 * @param {number} rows - Minimum height in rows (default 3)
 * @param {boolean} compact - Use compact mode for inline editing (default false)
 */
const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter text...', 
  label = null,
  rows = 3,
  compact = false,
  readOnly = false
}) => {
  const editorRef = useRef(null);
  const isEditingRef = useRef(false); // Track if user is actively editing
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false
  });
  
  // Initialize editor content when value changes externally
  useEffect(() => {
    // Skip update if user is actively editing to preserve cursor position
    if (isEditingRef.current) {
      return;
    }
    
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Only update if different to avoid cursor jumping
      const currentValue = editorRef.current.innerHTML;
      if (normalizeHtml(currentValue) !== normalizeHtml(value || '')) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);
  
  // Normalize HTML for comparison (handles browser differences)
  const normalizeHtml = (html) => {
    return html
      .replace(/<br\s*\/?>/gi, '<br>')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };
  
  // Update active format states based on cursor position
  const updateFormatStates = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline')
    });
  };
  
  // Apply formatting command
  const applyFormat = (command) => {
    document.execCommand(command, false, null);
    editorRef.current.focus();
    updateFormatStates();
    handleChange();
  };
  
  // Handle content changes
  const handleChange = () => {
    if (onChange) {
      const html = editorRef.current.innerHTML;
      // Replace browser's <div> with <br> for newlines
      const cleaned = html.replace(/<div>/g, '<br>').replace(/<\/div>/g, '');
      onChange(cleaned);
    }
  };
  
  // Handle focus - mark as editing
  const handleFocus = () => {
    isEditingRef.current = true;
  };
  
  // Handle blur - no longer editing
  const handleBlur = () => {
    isEditingRef.current = false;
  };
  
  // Handle cursor position changes to update active formats
  const handleSelectionChange = () => {
    if (document.activeElement === editorRef.current) {
      updateFormatStates();
    }
  };
  
  // Handle paste - sanitize to only allow formatting tags
  const handlePaste = (e) => {
    e.preventDefault();
    
    // Get pasted content (try HTML first, fall back to plain text)
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      // Sanitize HTML - allow only b, strong, i, em, u tags
      const cleaned = sanitizeHtml(html);
      document.execCommand('insertHTML', false, cleaned);
    } else {
      // Plain text - just insert it
      document.execCommand('insertText', false, text);
    }
    
    handleChange();
  };
  
  // Sanitize HTML to allow only formatting tags
  const sanitizeHtml = (html) => {
    // First, remove HTML comments (like <!--StartFragment-->, <!--EndFragment-->)
    let cleaned = html.replace(/<!--[\s\S]*?-->/g, '');
    
    // Remove common clipboard artifacts
    cleaned = cleaned.replace(/\n\t/g, ''); // Remove newline+tab combinations
    cleaned = cleaned.replace(/\r\n/g, ''); // Remove carriage returns
    cleaned = cleaned.replace(/\\/g, ''); // Remove escape backslashes
    
    // Create temporary element
    const temp = document.createElement('div');
    temp.innerHTML = cleaned;
    
    // Allowed tags
    const allowedTags = ['B', 'STRONG', 'I', 'EM', 'U', 'BR'];
    
    // Walk the DOM tree and remove disallowed tags
    const sanitizeNode = (node) => {
      const children = Array.from(node.childNodes);
      
      children.forEach(child => {
        if (child.nodeType === 1) { // Element node
          // Remove attributes (prevent onclick, style, etc.)
          while (child.attributes.length > 0) {
            child.removeAttribute(child.attributes[0].name);
          }
          
          if (!allowedTags.includes(child.tagName)) {
            // Replace disallowed tag with its text content
            const textNode = document.createTextNode(child.textContent);
            child.parentNode.replaceChild(textNode, child);
          } else {
            // Recursively sanitize children
            sanitizeNode(child);
          }
        } else if (child.nodeType === 3) { // Text node
          // Clean up text content
          let textContent = child.textContent;
          // Remove HTML comments that might be in text nodes
          textContent = textContent.replace(/<!--[\s\S]*?-->/g, '');
          // Clean up whitespace
          textContent = textContent.replace(/\s+/g, ' '); // Collapse multiple spaces
          child.textContent = textContent;
        }
      });
    };
    
    sanitizeNode(temp);
    
    // Final cleanup - trim and remove any remaining HTML artifacts
    let result = temp.innerHTML;
    result = result.trim();
    
    return result;
  };
  
  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Ctrl+B for bold
    if (e.ctrlKey && e.key === 'b') {
      e.preventDefault();
      applyFormat('bold');
    }
    // Ctrl+I for italic
    else if (e.ctrlKey && e.key === 'i') {
      e.preventDefault();
      applyFormat('italic');
    }
    // Ctrl+U for underline
    else if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      applyFormat('underline');
    }
  };
  
  // Set up selection change listener
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);
  
  // Toolbar button component
  const FormatButton = ({ icon: Icon, command, title, isActive }) => (
    <button
      type="button"
      onClick={() => applyFormat(command)}
      className={`p-1.5 rounded transition-colors ${
        isActive 
          ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' 
          : 'text-gray-600 hover:bg-gray-200'
      }`}
      title={title}
      tabIndex={-1}
    >
      <Icon size={compact ? 14 : 16} />
    </button>
  );
  
  return (
    <div className={`rich-text-editor ${compact ? 'compact' : ''}`}>
      {label && !compact && (
        <label className="block text-xs font-semibold text-gray-600 mb-2">
          {label}
        </label>
      )}
      
      {/* Formatting Toolbar - hidden in readOnly mode */}
      {!readOnly && (
        <div className={`flex gap-1 bg-gray-50 border border-gray-300 ${
          compact ? 'p-1 rounded-t-sm' : 'p-2 rounded-t'
        }`}>
          <FormatButton 
            icon={Bold} 
            command="bold" 
            title="Bold (Ctrl+B)"
            isActive={activeFormats.bold}
          />
          <FormatButton 
            icon={Italic} 
            command="italic" 
            title="Italic (Ctrl+I)"
            isActive={activeFormats.italic}
          />
          <FormatButton 
            icon={Underline} 
            command="underline" 
            title="Underline (Ctrl+U)"
            isActive={activeFormats.underline}
          />
          
          {!compact && (
            <>
              <div className="border-l border-gray-400 mx-1"></div>
              <button
                type="button"
                onClick={() => applyFormat('removeFormat')}
                className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                title="Clear Formatting"
                tabIndex={-1}
              >
                <Type size={16} />
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Editable Content Area */}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        className={`w-full text-sm border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:outline-none overflow-auto ${
          readOnly ? 'bg-gray-50 cursor-not-allowed rounded' : ''
        } ${
          !readOnly ? 'border-t-0' : ''
        } ${
          compact 
            ? 'px-2 py-1 rounded-b-sm min-h-[32px]' 
            : 'px-3 py-2 rounded-b min-h-[80px]'
        }`}
        style={{ minHeight: compact ? '32px' : `${rows * 24}px` }}
        onInput={readOnly ? undefined : handleChange}
        onPaste={readOnly ? undefined : handlePaste}
        onKeyDown={readOnly ? undefined : handleKeyDown}
        onFocus={readOnly ? undefined : handleFocus}
        onBlur={readOnly ? undefined : handleBlur}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
      
      <style>{`
        .rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor [contenteditable]:focus {
          outline: none;
        }
        .rich-text-editor [contenteditable] b,
        .rich-text-editor [contenteditable] strong {
          font-weight: 700;
        }
        .rich-text-editor [contenteditable] i,
        .rich-text-editor [contenteditable] em {
          font-style: italic;
        }
        .rich-text-editor [contenteditable] u {
          text-decoration: underline;
        }
        .rich-text-editor.compact [contenteditable] {
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
